/**
 * WeaveIn.ai Platform - Signature Viewer
 * Displays a captured signature with signer details, timestamp, and proof download.
 */

import React, { useState, useEffect } from 'react';
import {
  CheckCircle, Shield, Clock, User, Download, ExternalLink, Hash, Eye,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { config } from '@/src/config';

interface SignatureData {
  id: number;
  uuid: string;
  signature_type: string;
  signature_data: string;
  signature_text: string | null;
  signature_font: string | null;
  signer_name: string;
  signer_email: string | null;
  signer_ip: string | null;
  signed_at: string;
  consent_text: string;
  consent_accepted_at: string;
  signature_hash: string;
  is_verified: boolean;
  request_title: string;
  proof_url: string;
}

interface SignatureViewerProps {
  stepInstanceId?: number;
  signatureId?: number;
  requestId?: number;
  compact?: boolean;
}

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const cboId = localStorage.getItem('active_cbo_id');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(cboId && { 'X-CBO-ID': cboId }),
  };
};

export function SignatureViewer({ stepInstanceId, signatureId, requestId, compact = false }: SignatureViewerProps) {
  const [sig, setSig] = useState<SignatureData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSignature = async () => {
      try {
        let url: string;
        if (stepInstanceId) {
          url = `${config.weaveinApi.baseUrl}/api/signatures/by-step/${stepInstanceId}`;
        } else if (requestId) {
          url = `${config.weaveinApi.baseUrl}/api/signatures/by-request/${requestId}`;
        } else if (signatureId) {
          url = `${config.weaveinApi.baseUrl}/api/signatures/by-request/${signatureId}`;
        } else {
          setLoading(false);
          return;
        }

        const res = await fetch(url, { credentials: 'include', headers: getAuthHeaders() });
        if (res.ok) {
          setSig(await res.json());
        } else if (res.status === 404) {
          setSig(null);
        } else {
          setError('Failed to load signature');
        }
      } catch (e) {
        setError('Could not fetch signature');
      } finally {
        setLoading(false);
      }
    };
    fetchSignature();
  }, [stepInstanceId, signatureId]);

  if (loading) return <div className="text-xs text-muted-foreground">Loading signature...</div>;
  if (error || !sig) return null;

  const openProof = () => {
    const token = localStorage.getItem('auth_token');
    const cboId = localStorage.getItem('active_cbo_id');
    const url = `${config.weaveinApi.baseUrl}${sig.proof_url}`;
    // Open proof in new window with auth
    fetch(url, { headers: getAuthHeaders() })
      .then(r => r.text())
      .then(html => {
        const win = window.open('', '_blank');
        if (win) {
          win.document.write(html);
          win.document.close();
        }
      })
      .catch(() => window.open(url, '_blank'));
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2 mt-2 p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium text-green-800 dark:text-green-300">
            Signed by {sig.signer_name}
          </div>
          <div className="text-xs text-green-600 dark:text-green-400">
            {new Date(sig.signed_at).toLocaleString()} • {sig.signature_type}
          </div>
        </div>
        {sig.signature_data && sig.signature_data.startsWith('data:image') && (
          <img src={sig.signature_data} alt="Signature" className="h-8 opacity-80" />
        )}
        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={openProof}>
          <Download className="w-3 h-3" />
        </Button>
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-4 bg-white dark:bg-gray-900 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-green-600" />
          <h4 className="font-medium text-sm">Electronic Signature</h4>
          <Badge className="bg-green-100 text-green-800 text-xs">
            {sig.is_verified ? 'Verified' : 'Unverified'}
          </Badge>
        </div>
        <Button size="sm" variant="outline" onClick={openProof}>
          <Download className="w-3 h-3 mr-1" /> Proof Certificate
        </Button>
      </div>

      {/* Signature Image */}
      {sig.signature_data && sig.signature_data.startsWith('data:image') && (
        <div className="border rounded-md p-4 bg-gray-50 dark:bg-gray-800 text-center">
          <img src={sig.signature_data} alt="Signature" className="max-h-20 mx-auto" />
          <div className="border-t border-gray-200 dark:border-gray-700 mt-2 pt-1">
            <span className="text-xs text-gray-400">Electronic Signature</span>
          </div>
        </div>
      )}

      {/* Signer Details */}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="flex items-center gap-1.5">
          <User className="w-3 h-3 text-muted-foreground" />
          <span className="font-medium">Signer:</span> {sig.signer_name}
        </div>
        <div className="flex items-center gap-1.5">
          <Clock className="w-3 h-3 text-muted-foreground" />
          <span className="font-medium">Signed:</span> {new Date(sig.signed_at).toLocaleString()}
        </div>
        <div className="flex items-center gap-1.5">
          <Eye className="w-3 h-3 text-muted-foreground" />
          <span className="font-medium">Type:</span> {sig.signature_type}
        </div>
        {sig.signer_ip && (
          <div className="flex items-center gap-1.5">
            <Hash className="w-3 h-3 text-muted-foreground" />
            <span className="font-medium">IP:</span> {sig.signer_ip}
          </div>
        )}
      </div>

      {/* Hash */}
      <div className="text-xs text-muted-foreground font-mono bg-muted/50 p-2 rounded truncate">
        SHA-256: {sig.signature_hash}
      </div>

      {/* Consent */}
      <div className="text-xs text-muted-foreground italic">
        "{sig.consent_text}"
      </div>
    </div>
  );
}
